import type { ComputedRef, Ref } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { useTeamsStore } from './store'
import { useTeamCreate } from '../../api/teams/create.hook'
import { useTeamsApi } from '../../api/teams/list.hook'
import { toMemberOptions } from './team-options'

interface CreateTeamReturn {
  isOpen: ComputedRef<boolean>
  refName: Ref<string>
  refMembers: Ref<number[]>
  memberOptions: ComputedRef<AppSelectOption<number>[]>
  isSaving: ComputedRef<boolean>
  isSaveDisabled: ComputedRef<boolean>
  save: () => Promise<void>
  cancel: () => void
}

export const useCreateTeam = (): CreateTeamReturn => {
  const store = useTeamsStore()

  const isOpen = computed<boolean>(() => store.isModalCreate)
  const refName = ref<string>('')
  const refMembers = ref<number[]>([])

  const memberOptions = computed<AppSelectOption<number>[]>(() => toMemberOptions(store.getUsers))
  const isSaving = computed<boolean>(() => store.isSaving)
  const isSaveDisabled = computed<boolean>(() => !refName.value.trim() || refMembers.value.length === 0)

  const reset = (): void => {
    refName.value = ''
    refMembers.value = []
  }

  const cancel = (): void => {
    store.setModalCreate(false)
  }

  const save = async (): Promise<void> => {
    const created = await useTeamCreate({ name: refName.value.trim(), users: refMembers.value })

    if (created) {
      store.setModalCreate(false)
      await useTeamsApi()
    }
  }

  // Start each open with an empty form.
  watch(isOpen, (open) => {
    if (open) reset()
  })

  return { isOpen, refName, refMembers, memberOptions, isSaving, isSaveDisabled, save, cancel }
}
